"use client"

import * as React from "react"
import * as ToastPrimitives from "@radix-ui/react-toast"
import { cva, type VariantProps } from "class-variance-authority"
import { useTranslations } from "next-intl"
import { Icon } from "./icon"

import { cn } from "@/lib/utils"

const ToastProvider = ToastPrimitives.Provider

// MD3 snackbar viewport:
// - Mobile: top inset (top-4 left-4 right-4) — clears the bottom-right FAB and
//   the bottom-left Notion overlay.
// - Desktop (>= sm): bottom-right cluster, capped to max-w-md.
const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      "fixed top-4 right-4 left-4 z-[var(--z-toast)] flex max-h-screen flex-col gap-2",
      "md:top-auto md:left-auto md:bottom-6 md:right-6 md:w-full md:max-w-md md:p-0",
      className
    )}
    {...props}
  />
))
ToastViewport.displayName = ToastPrimitives.Viewport.displayName

// MD3 snackbar surface:
// - Default: inverse-surface / inverse-on-surface (foreground/background).
// - Tonal variants use *-container / on-*-container token pairs.
// - Single-line target h-12; multi-line grows with min-h-14 and max-w 480px.
// - Animation: slide in from bottom on mobile, from right on desktop, 240ms
//   ease-emphasized-decel.
const toastVariants = cva(
  cn(
    "group pointer-events-auto relative flex w-full items-center gap-3 overflow-hidden rounded-xl border border-outline-variant bg-surface-1 px-3 py-2 text-foreground shadow-[var(--elev-2)] md:px-4 md:py-3",
    "sm:max-w-[480px]",
    "duration-[var(--duration-medium-1)] ease-[var(--ease-emphasized-decel)]",
    "data-[state=open]:animate-in data-[state=closed]:animate-out",
    "data-[state=open]:slide-in-from-top-4 data-[state=closed]:slide-out-to-top-4",
    "md:data-[state=open]:slide-in-from-right-full md:data-[state=closed]:slide-out-to-right-full",
    "data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
    "data-[swipe=cancel]:translate-x-0 data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=end]:animate-out",
    "motion-reduce:animate-none"
  ),
  {
    variants: {
      variant: {
        // Industrial register: full hairline border + solid tonal container
        // carries the status. Tonal containers (success-container, error-
        // container, etc.) are designed for opaque use on their own; the /40
        // overlay was making the surface see-through against the page.
        default: "",
        success: "bg-success-container text-on-success-container border-success/30",
        error: "bg-error-container text-on-error-container border-error/30",
        warning: "bg-warning-container text-on-warning-container border-warning/30",
        info: "bg-info-container text-on-info-container border-info/30",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

// Map tone variant → Radix announcer type so SRs get the right urgency:
// - error/warning announce assertively (interrupts; role="alert", aria-live="assertive")
// - all other variants announce politely (role="status", aria-live="polite")
// Consumers can still override via the `type` prop.
function getAnnouncerType(
  variant: VariantProps<typeof toastVariants>["variant"]
) {
  if (variant === "error" || variant === "warning") {
    return "foreground" as const
  }
  return "background" as const
}

const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> &
    VariantProps<typeof toastVariants>
>(({ className, variant, type, ...props }, ref) => {
  return (
    <ToastPrimitives.Root
      ref={ref}
      type={type !== undefined ? type : getAnnouncerType(variant)}
      className={cn(toastVariants({ variant }), className)}
      {...props}
    />
  )
})
Toast.displayName = ToastPrimitives.Root.displayName

// Action inherits the toast's on-surface color via currentColor so it stays
// legible across both the inverse-surface default and tonal variants.
const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      "inline-flex h-8 shrink-0 items-center justify-center rounded-full px-3 type-label-l font-medium",
      "transition-colors duration-[var(--duration-short-4)] ease-[var(--ease-emphasized)] motion-reduce:transition-none",
      "hover:bg-current/10 focus-visible:bg-current/15 active:bg-current/15",
      "focus:outline-none focus-visible:ring-2 focus-visible:ring-current/60",
      "disabled:pointer-events-none disabled:opacity-50",
      className
    )}
    {...props}
  />
))
ToastAction.displayName = ToastPrimitives.Action.displayName

const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => {
  const translate = useTranslations()
  return (
    <ToastPrimitives.Close
      ref={ref}
      className={cn(
        "inline-flex size-8 shrink-0 items-center justify-center rounded-full opacity-80",
        "transition-colors duration-[var(--duration-short-4)] ease-[var(--ease-emphasized)] motion-reduce:transition-none",
        "hover:bg-current/10 hover:opacity-100 focus-visible:bg-current/15",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-current/60",
        className
      )}
      toast-close=""
      {...props}
    >
      <Icon icon="lucide:x" size={18} />
      <span className="sr-only">{translate('common.actions.close')}</span>
    </ToastPrimitives.Close>
  )
})
ToastClose.displayName = ToastPrimitives.Close.displayName


const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    className={cn("type-label-l font-medium", className)}
    {...props}
  />
))
ToastTitle.displayName = ToastPrimitives.Title.displayName

const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={cn("type-body-s mt-0.5 text-on-surface-variant", className)}
    {...props}
  />
))
ToastDescription.displayName = ToastPrimitives.Description.displayName

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>

type ToastActionElement = React.ReactElement<typeof ToastAction>

export {
  type ToastProps,
  type ToastActionElement,
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
}
